import React, { FC, useMemo } from 'react';

import {
  logout,
  getAccOrigin,
  getUserProfileUrl,
} from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { clearNetworkCache } from '@adsk/alloy-helpers';
import A from '@adsk/alloy-react-a';
import { Box } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import { WithAlloySuspense } from '@adsk/alloy-react-suspense';

import { ANALYTICS_TYPES } from '../helpers/analytics';
import getNotificationSettingsUrl from '../helpers/getNotificationSettingsUrl';
import { UserType } from '../helpers/types';
import { useTranslation } from '../i18n';

type ProfileProps = {
  showSubscriptionsModal?: boolean;
  user: UserType;
  projectId?: string;
  onLogout?: () => void;
  onClose: () => void;
  onOpenSubscriptions: () => void;
  UNSAFE_showBuildersUserProfileModal?: () => void;
  UNSAFE_isBuildersProfileModalEnabled?: boolean;
};

/**
 * Acs TopNavigation
 */
const Profile: FC<ProfileProps> = ({
  showSubscriptionsModal,
  user,
  projectId,
  onLogout,
  onClose,
  onOpenSubscriptions,
  UNSAFE_showBuildersUserProfileModal: showBuildersUserProfileModal,
  UNSAFE_isBuildersProfileModalEnabled: isBuildersProfileModalEnabled,
}) => {
  const { t } = useTranslation();
  const userProfileUrl = useMemo(() => getUserProfileUrl(), []);
  return (
    <Box width="250px" padding="24px">
      <Box typography="heading4" color="charcoal900">
        {user.firstName} {user.lastName}
      </Box>
      <Box typography="bodySmall" color="charcoal600">
        {user.email}
      </Box>
      {showSubscriptionsModal && (
        <Box typography="bodySmall" color="charcoal600" marginTop="10px">
          <a
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onClose();
              analytics.track(
                ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_MYSUBSCRIPTIONS_CLICK,
              );
              onOpenSubscriptions();
            }}
            data-testid="My__Subscriptions__Label"
            rel="noopener noreferrer"
          >
            {t('MY_SUBSCRIPTIONS')}
          </a>
        </Box>
      )}

      {projectId && (
        <Box typography="bodySmall" color="charcoal600" marginTop="10px">
          <A
            href={getNotificationSettingsUrl(projectId)}
            rel="noopener noreferrer"
            onClick={() =>
              analytics.track(
                ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_NOTIFICATIONSETTINGS_CLICK,
              )
            }
          >
            {t('NOTIFICATION_SETTINGS')}
          </A>
        </Box>
      )}

      {isBuildersProfileModalEnabled && (
        <Box typography="bodySmall" color="charcoal600" marginTop="10px">
          <a
            style={{ cursor: 'pointer' }}
            onClick={() => {
              onClose();
              analytics.track(
                ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_EDIT_PROFILE_CLICK,
              );
              showBuildersUserProfileModal?.();
            }}
            data-testid="Builders_Network_Edit__Profile__Label"
            rel="noopener noreferrer"
          >
            {t('EDIT_PROFILE')}
          </a>
        </Box>
      )}

      <Box paddingTop="32px">
        {!isBuildersProfileModalEnabled && (
          <Button
            as="a"
            href={userProfileUrl}
            rel="noopener noreferrer"
            target="_blank"
            variant={Button.VARIANTS.PRIMARY}
            size={Button.SIZES.SMALL}
            style={{ marginRight: 12 }}
            onClick={() => {
              analytics.track(
                ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_PROFILESETTINGS_CLICK,
              );
              onClose();
            }}
          >
            {t('PROFILE_SETTINGS')}
          </Button>
        )}

        <Button
          variant={Button.VARIANTS.SECONDARY}
          size={Button.SIZES.SMALL}
          onClick={() => {
            clearNetworkCache();
            analytics.track(
              ANALYTICS_TYPES.TOPNAVIGATION_USERAREA_SIGNOUT_CLICK,
            );
            if (onLogout) {
              onLogout();
            }
            logout(getAccOrigin());
            onClose();
          }}
        >
          {t('SIGN_OUT')}
        </Button>
      </Box>
    </Box>
  );
};

Profile.displayName = 'Profile';

export default WithAlloySuspense(Profile);
